import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import _ from 'underscore';

import LiveManagerSnapshotSettings from './LiveManagerSnapshotSettings/LiveManagerSnapshotSettings';
import LiveManagerStreamSettings from './LiveManagerStreamSettings/LiveManagerStreamSettings';
import LiveBroadcasterSettings from './LiveBroadcasterSettings/LiveBroadcasterSettings';
import LiveManagerInteractQueue from './LiveManagerInteractQueue/LiveManagerInteractQueue';
import LiveManagerPresenter from './LiveManagerPresenter/LiveManagerPresenter';
import TemporaryAddParticipantSection from './LiveManagerParticipants/TemporaryAddParticipantSection';

import { setLiveSettingsId } from '../../actions/liveManagerActions';
import IconsStore from '../../reusable/IconsStore/IconsStore';
import { IconsList } from '../../reusable/IconsStore/IconsList';
import { liveSettingsList } from '../../constants/liveManagerConstants';
import { interactTimelineItemTypes } from '../../constants/interactConstant';
import { checkPermissionGrantedFor } from '../../services/componentReusableService';
import { liveWebSocketEvents } from '../../constants/liveChatConstant';
import { chatWebSocketUrl } from '../../utils/config';

import ViewerEngagement from './ViewEngagement/ViewerEngagement';

import './LiveManagerSettings.scss';

class LiveManagerSettings extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {};
		this.webSocketConnection = null;
		this.socketMessageHandlers = [];
	}

	componentDidMount() {
		this.connectWebSocket();
	}

	componentWillUnmount() {
		this.webSocketConnection?.close(3001);
	}

	checkSocket = () => {
		const connection = this.webSocketConnection;
		if (!connection) { return false; }
		return connection.readyState === 1;
	};

	sendData = () => {
		const { defaultAccountId, mediaId } = this.props;
		const connection = this.webSocketConnection;
		const keys = [
			liveWebSocketEvents.textCreated,
			liveWebSocketEvents.textUpdated,
			liveWebSocketEvents.textPinned,
			liveWebSocketEvents.textUnPinned,
			liveWebSocketEvents.reactionCreated,
			liveWebSocketEvents.reactionUpdated,
			liveWebSocketEvents.blockMessage,
			liveWebSocketEvents.blockUser,
			liveWebSocketEvents.unblockMessage,
			liveWebSocketEvents.unblockUser,
			liveWebSocketEvents.slide,
		];
		const request = {
			key: '',
			body: {
				filter: {
					tenantId: defaultAccountId,
					mediaId: mediaId,
				},
			},
		};

		for (let i = 0; i < keys.length; i++) {
			request.key = keys[i];
			connection.send(JSON.stringify(request));
		}
	};

	connectWebSocket = () => {
		if (this.webSocketConnection?.close) {
			this.webSocketConnection.close();
		}

		this.webSocketConnection = new WebSocket(chatWebSocketUrl ?? '');
		this.webSocketConnection.onopen = () => {
			if (this.checkSocket()) {
				this.sendData();
			}
		};

		this.webSocketConnection.onmessage = (evt) => {
			this.handleSocketMessage(evt);
			this.socketMessageHandlers.forEach((handler) => handler(evt));
		};

		this.webSocketConnection.onerror = () => {
			this.webSocketConnection?.close();
		};

		this.webSocketConnection.onclose = (event) => {
			if (event.code === 3001) { return; }
			setTimeout(() => {
				if (!this.checkSocket()) {
					this.connectWebSocket();
				}
			}, 1000);
		};
	};

	handleSocketMessage = (_evt) => {
		// Handle socket messages at the LiveManagerSettings level here
	};

	registerSocketMessageHandler = (handler) => {
		this.socketMessageHandlers.push(handler);
	};

	unregisterSocketMessageHandler = (handler) => {
		this.socketMessageHandlers = this.socketMessageHandlers.filter((h) => h !== handler);
	};

	renderStreamSettings = () => {
		const { t, mediaId, isVisible, SaveAsOnDemand } = this.props;
		return (
			<>
				<LiveManagerStreamSettings
					isVisible={isVisible}
					t={t}
					mediaId={mediaId}
					onStreamSettingsLoaded={this.onStreamSettingsLoaded}
					SaveAsOnDemand={SaveAsOnDemand}
				/>
			</>
		);
	};

	renderInteractSettings = () => {
		const { t, mediaId, showMessage } = this.props;
		return (
			<>
				<LiveManagerInteractQueue
					t={t}
					mediaId={mediaId}
					showMessage={showMessage}
					liveInteractSave={this.props.liveInteractSave}
				/>
			</>
		);
	};

	renderSnapshotSettings = () => {
		const { t, mediaId } = this.props;
		return (
			<>
				<LiveManagerSnapshotSettings
					t={t}
					mediaId={mediaId}
					defaultEmails={this.props.defaultEmails}
				></LiveManagerSnapshotSettings>
			</>
		);
	};

	renderViewerEngagement = () => {
		const { mediaId, mediaDetails, reloadData, moderatorMode } = this.props;
		return (
			<ViewerEngagement
				backToList={this.backToList}
				mediaId={mediaId}
				mediaDetails={mediaDetails}
				reloadData={reloadData}
				moderatorMode={moderatorMode}
				registerSocketMessageHandler={this.registerSocketMessageHandler}
				unregisterSocketMessageHandler={this.unregisterSocketMessageHandler}
			/>
		);
	};

	renderBroadcasterSettings = () => {
		const { t, mediaId } = this.props;
		return <>{<LiveBroadcasterSettings t={t} mediaId={mediaId}></LiveBroadcasterSettings>}</>;
	};

	renderPresenter = () => {
		const { mediaId, reloadData } = this.props;
		return <LiveManagerPresenter
			mediaId={mediaId}
			registerSocketMessageHandler={this.registerSocketMessageHandler}
			unregisterSocketMessageHandler={this.unregisterSocketMessageHandler}
			reloadData={reloadData}
		/>;
	};

	renderParticipants = () => {
		const { mediaId, mediaDetails } = this.props;
		const linkedFormId = mediaDetails?.links?.findLast((i) => i.type === 'Form-meet')?.id || '';
		return <TemporaryAddParticipantSection mediaId={mediaId} mediaDetails={mediaDetails} linkedFormId={linkedFormId} />;
	};

	handleSettingsSelection = (id) => {
		this.props.setLiveSettingsId(id);
	};

	backToList = () => {
		this.props.setLiveSettingsId('');
	};

	render() {
		const { selectedItem, liveSettingsId, t, timelineItems, userPermissions } = this.props;
		let item = _.find(timelineItems ? timelineItems : this.props.timelineItems, (item) => {
			return item.id === selectedItem;
		});
		let settingsText = '';
		if (liveSettingsId === liveSettingsList.STREAM) {
			settingsText = settingsText + t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_STREAM_SETTINGS');
		} else if (liveSettingsId === liveSettingsList.INTERACT) {
			settingsText = settingsText + t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_INTERACT_QUEUE');
		} else if (liveSettingsId === liveSettingsList.SNAPSHOT && item) {
			settingsText = settingsText + (item && item.content ? item.content : '');
		} else if (liveSettingsId === liveSettingsList.CHAT) {
			settingsText = settingsText + t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_INCOMING_CHAT');
		} else if (liveSettingsId === liveSettingsList.PRESENTER) {
			settingsText = settingsText + t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER');
		} else if (liveSettingsId === liveSettingsList.PARTICIPANTS) {
			settingsText = settingsText + t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PARTICIPANTS');
		} /*  else if (liveSettingsId === liveSettingsList.BROADCASTER) {
            settingsText = settingsText + t('BROADCASTER')
        } */

		return (
			<>
				<div id="liveStreamSettings">
					{!liveSettingsId && (
						<ul className="streamSettingsOption">
							<li onClick={() => this.handleSettingsSelection(liveSettingsList.STREAM)}>
								{' '}
								{<IconsStore iconName={IconsList.SETTINGS} />}
								<label>
									{' '}
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_STREAM_SETTINGS')}{' '}
								</label>{' '}
							</li>
							<li onClick={() => this.handleSettingsSelection(liveSettingsList.INTERACT)}>
								{' '}
								<IconsStore iconName={IconsList.INTERACT} />
								<label>
									{' '}
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_INTERACT_QUEUE')}{' '}
								</label>{' '}
							</li>
							<li onClick={() => this.handleSettingsSelection(liveSettingsList.CHAT)}>
								{' '}
								<IconsStore iconName={IconsList.CHAT_LEFT_DOTS} />
								<label>
									{' '}
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_INCOMING_CHAT')}{' '}
								</label>{' '}
							</li>
							{checkPermissionGrantedFor(userPermissions, 'forms') && (
								<li onClick={() => this.handleSettingsSelection(liveSettingsList.PRESENTER)}>
									<IconsStore iconName={IconsList.CHAT_LEFT_DOTS} />
									<label> {t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PRESENTER')} </label>{' '}
								</li>
							)}
							<li onClick={() => this.handleSettingsSelection(liveSettingsList.PARTICIPANTS)}>
								{' '}
								<IconsStore iconName={IconsList.PEOPLE} />
								<label>
									{' '}
									{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PARTICIPANTS')}{' '}
								</label>{' '}
							</li>
							{/*
                            <li onClick={(e) => this.handleSettingsSelection(liveSettingsList.BROADCASTER)}>  <IconsStore iconName={IconsList.BROADCASTER_ICON} />
                                <label> {t('BROADCASTER')} </label> </li> */}
						</ul>
					)}
					{liveSettingsId && liveSettingsId !== liveSettingsList.CHAT && (
						<div className="liveSettingsHeader">
							<div className="backButton" onClick={() => this.backToList()}>
								{<IconsStore iconName={IconsList.LEFT_ARROW} />}
							</div>
							<label>{settingsText}</label>
						</div>
					)}

					{liveSettingsId === liveSettingsList.STREAM ? (
						<> {this.renderStreamSettings()} </>
					) : liveSettingsId === liveSettingsList.INTERACT ? (
						<> {this.renderInteractSettings()} </>
					) : (
						<>
							{' '}
							{liveSettingsId === liveSettingsList.SNAPSHOT && item && item.itemType === interactTimelineItemTypes.slide
								? this.renderPresenter()
								: liveSettingsId === liveSettingsList.SNAPSHOT && item
								? this.renderSnapshotSettings()
								: null}
							{' '}
						</>
					)}
					{liveSettingsId === liveSettingsList.CHAT && this.renderViewerEngagement()}
					{liveSettingsId === liveSettingsList.PRESENTER && this.renderPresenter()}
					{liveSettingsId === liveSettingsList.PARTICIPANTS && this.renderParticipants()}
				</div>
			</>
		);
	}
}

LiveManagerSnapshotSettings.propTypes = {
	mediaId: PropTypes.string.isRequired,
};

LiveManagerSnapshotSettings.defaultProps = {
	// showSettings: false
};

const mapStateToProps = ({ liveManager, session }) => ({
	liveSettingsId: liveManager.liveSettingsId,
	selectedItem: liveManager.selectedItem,
	timelineItems: liveManager.items,
	isPlayerAtLivePosition: liveManager.isPlayerAtLivePosition,
	userPermissions: session.userPermissions,
	defaultAccountId: session.defaultAccountId,
});

const mapDispatchToProps = (dispatch) => ({
	setLiveSettingsId: (id) => dispatch(setLiveSettingsId(id)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LiveManagerSettings));
